/* Permanent, separately priced services attached to the original cooker. */
(function (root) {
  'use strict';
  function cents(value) {
    const text = String(value).trim().replace(/^£\s*/, '').replace(/,/g, '');
    if (!/^\d+(\.\d{1,2})?$/.test(text)) throw new Error('Invalid service price.');
    return Math.round(Number(text) * 100);
  }

  function parseOption(value) {
    const match = /^\s*£?\s*(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?\s*,\s*(.+)$/s.exec(value);
    if (!match) throw new Error('The service price is not configured correctly. Please contact us.');
    return { price: cents(`${match[1]}.${(match[2] || '').padEnd(2, '0')}`), label: match[3].trim().replace(/\s+/g, ' ') };
  }

  function buildItems({ variantId, quantity, selections, catalogue, properties = {}, group, title, currency }) {
    if (!Number.isSafeInteger(Number(variantId)) || Number(variantId) <= 0 || !Number.isInteger(quantity) || quantity < 1) {
      throw new Error('Please select a cooker and a valid quantity.');
    }
    const paid = selections.filter(s => s.price > 0);
    if (paid.length && currency !== 'GBP') throw new Error('Please select GBP to order installation services.');
    const cookerProperties = { ...properties };
    selections.filter(s => s.price === 0).forEach((s, i) => { cookerProperties[`Included service ${i + 1}`] = s.label; });
    if (paid.length) Object.assign(cookerProperties, { _diw_group: group, _diw_role: 'cooker' });
    const cooker = { id: Number(variantId), quantity, properties: cookerProperties };
    const services = paid.map(s => {
      const matches = catalogue.filter(v => v.selectionKey === `${s.type}:${s.optionIndex}`);
      if (matches.length !== 1 || !matches[0].available || matches[0].price !== s.price || matches[0].requires_shipping !== false ||
          (matches[0].label !== undefined && matches[0].label !== s.label)) {
        throw new Error(`“${s.label}” is currently unavailable. Please deselect it or contact us to arrange the service.`);
      }
      return {
        id: matches[0].id, quantity, parent_id: cooker.id,
        properties: { Service: s.label, 'For cooker': title, _diw_group: group, _diw_role: 'service' }
      };
    });
    return [cooker, ...services];
  }

  function serviceUpdates(cart) {
    const parents = new Map(cart.items.filter(i => i.properties?._diw_role === 'cooker').map(i => [i.properties._diw_group, i]));
    return cart.items.filter(i => i.properties?._diw_role === 'service').flatMap(i => {
      const parent = parents.get(i.properties._diw_group);
      const quantity = parent?.quantity || 0;
      return quantity === i.quantity ? [] : [{ id: i.key, quantity }];
    });
  }

  const api = { cents, parseOption, buildItems, serviceUpdates };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document) return;
  root.CookerServices = api;
  const route = path => (root.Shopify?.routes?.root || '/') + path;
  async function request(path, data) {
    const response = await fetch(route(path), data ? {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
    } : { cache: 'no-store' });
    const result = await response.json();
    if (!response.ok) {
      const error = new Error(typeof result.description === 'string' ? result.description : 'Unable to update your cart. Please try again.');
      error.rejected = response.status >= 400 && response.status < 500;
      throw error;
    }
    return result;
  }

  // Validate the cooker quantity with change.js first. Never use update.js to
  // increase cooker stock: it does not validate existing-line inventory.
  let queue = Promise.resolve();
  function serial(task) {
    const next = queue.then(task);
    queue = next.catch(() => {});
    return next;
  }
  async function reconcile() {
    let cart = await request('cart.js');
    const maxAttempts = cart.items.length + 1;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const update = serviceUpdates(cart)[0];
      if (!update) return cart;
      await request('cart/change.js', update);
      // App discounts can change line keys. Always read them again.
      cart = await request('cart.js');
    }
    throw new Error('Please refresh your cart to confirm the selected service quantities.');
  }
  api.change = (key, quantity) => serial(async () => {
    if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Please enter a valid quantity.');
    await request('cart/change.js', { id: key, quantity });
    return reconcile();
  });
  api.reconcile = () => serial(reconcile);

  function initialize(configNode) {
    if (configNode.dataset.initialized) return;
    configNode.dataset.initialized = 'true';
    const config = JSON.parse(configNode.textContent);
    const section = document.getElementById(`shopify-section-${config.sectionId}`);
    const form = document.getElementById(`product-form-${config.sectionId}`);
    const options = section?.querySelector('.product-custom-options');
    if (!form || !options) return;
    const checkboxes = [...options.querySelectorAll('input[type="checkbox"]')];
    const optionFor = b => {
      const optionIndex = checkboxes.filter(other => other.name === b.name).indexOf(b);
      const source = config.optionSources?.[b.name];
      const value = Array.isArray(source) ? source[optionIndex] : source;
      return { type: b.name, optionIndex, ...(value != null ? parseOption(value) : {
        price: cents(b.value), label: b.closest('label').textContent.trim().replace(/\s+/g, ' ')
      }) };
    };
    // Read the same raw metafields as the backend, including commas in labels
    // and thousands separators. Older Liquid markup splits on every comma.
    checkboxes.forEach(b => {
      if (!config.optionSources?.[b.name]) return;
      try {
        const option = optionFor(b), label = b.closest('label');
        b.value = (option.price / 100).toFixed(2);
        b.checked = option.price === 0;
        [...label.childNodes].filter(node => node.nodeType === 3).forEach(node => node.remove());
        label.append(document.createTextNode(` ${option.label}`));
      } catch (_) { /* Invalid configuration is reported on submit. */ }
    });
    const selected = () => checkboxes.filter(b => b.checked).map(optionFor);
    const error = document.createElement('p');
    error.className = 'alert alert--error';
    error.setAttribute('role', 'alert');
    error.hidden = true;
    // Keep the error box out of the DOM until there is an actual error.
    // The theme alert styles override the browser default for [hidden].
    const button = form.querySelector('[name="add"]');
    const quantity = () => Number(form.querySelector('[name="quantity"]')?.value || 1);
    function updateTotal() {
      const total = section.querySelector('#cf-total');
      if (!total) return;
      const variant = config.variants.find(v => String(v.id) === form.querySelector('[name="id"]')?.value);
      try {
        const amount = ((variant?.price || 0) + selected().reduce((sum, s) => sum + s.price, 0)) * quantity();
        total.textContent = new Intl.NumberFormat('en-GB', { style: 'currency', currency: config.currency }).format(amount / 100);
      } catch (_) { /* Invalid pricing is reported on submit. */ }
    }
    checkboxes.forEach(b => b.addEventListener('change', updateTotal));
    form.addEventListener('input', updateTotal);
    form.addEventListener('change', updateTotal);
    section.addEventListener('VARIANT_CHANGE', updateTotal);
    updateTotal();
    let busy = false;
    let uncertain = false;
    form.addEventListener('submit', async event => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) return;
      if (uncertain) {
        location.assign(route('cart'));
        return;
      }
      busy = true;
      const wasDisabled = button?.disabled;
      if (button) { button.disabled = true; button.classList.add('working'); }
      error.hidden = true;
      if (error.isConnected) error.remove();
      let added = false;
      try {
        const data = new FormData(form);
        const properties = {};
        for (const [key, value] of data) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match) properties[match[1]] = value;
        }
        // Snapshot the selection and cooker before the server request, so a
        // colour/quantity change while it is in flight cannot mix cart items.
        const variantId = data.get('id'), selectedQuantity = quantity(), selections = selected();
        let catalogue = [];
        if (selections.some(s => s.price > 0)) {
          if (config.currency !== 'GBP') throw new Error('Please select GBP to order installation services.');
          const response = await fetch(config.serviceEndpoint, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ variantId, currency: config.currency,
              selections: selections.filter(s => s.price > 0).map(s => ({ type: s.type, optionIndex: s.optionIndex })) })
          });
          let result;
          try { result = await response.json(); } catch (_) {
            throw new Error('Service booking is temporarily unavailable. Please try again later or contact us.');
          }
          if (!response.ok || !result.success || !Array.isArray(result.catalogue)) {
            throw new Error(result.message || 'Unable to prepare the selected services. Please try again.');
          }
          catalogue = result.catalogue;
        }
        const items = buildItems({ variantId, quantity: selectedQuantity, selections,
          catalogue, properties, group: crypto.randomUUID(), title: config.title, currency: config.currency });
        if (data.get('selling_plan')) items[0].selling_plan = data.get('selling_plan');
        await serial(async () => {
          // A network timeout does not prove that Shopify rejected the add.
          uncertain = true;
          await request('cart/add.js', { items });
          added = true;
          uncertain = false;
        });
        if (!form.closest('product-form')?.hasAttribute('data-ajax-cart')) {
          location.assign(route('cart'));
          return;
        }
        const response = await fetch(location.pathname + '?section_id=helper-cart');
        if (!response.ok) throw new Error('Cart refresh failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        for (const id of ['AjaxCartForm', 'AjaxCartSubtotal']) {
          const current = document.getElementById(id), replacement = doc.getElementById(id);
          if (!current || !replacement) throw new Error('Cart refresh failed');
          current.innerHTML = replacement.innerHTML;
          current.ajaxifyCartItems?.();
        }
        for (const [target, source] of [['data-header-cart-count', 'data-cart-count'], ['data-header-cart-total', 'data-cart-total']]) {
          document.querySelectorAll(`[${target}]`).forEach(el => { el.textContent = doc.querySelector(`[${source}]`)?.textContent || ''; });
        }
        form.closest('product-form')?.dispatchEvent(new Event('add-to-cart'));
        document.querySelector('[data-js-site-cart-sidebar]')?.show();
      } catch (err) {
        if (added) { location.assign(route('cart')); return; }
        if (err.rejected) uncertain = false;
        error.textContent = uncertain ? 'Please check your cart before trying again. Your request may already have been received. ' + err.message : err.message;
        error.hidden = false;
        options.append(error);
      } finally {
        busy = false;
        if (button) { button.disabled = wasDisabled; button.classList.remove('working'); }
      }
    }, { capture: true });
  }

  function scan() { document.querySelectorAll('[data-cooker-services-config]').forEach(initialize); }
  // Other deferred theme scripts populate the form from its template first.
  if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  document.addEventListener('shopify:section:load', scan);

  // Reconcile before normal checkout, including after an interrupted quantity
  // request. A failed reconciliation leaves the customer on the cart to retry.
  let checkingOut = false;
  async function checkoutHandler(event) {
    const checkout = event.type === 'submit' ? event.submitter : event.target.closest('[name="checkout"], a[href$="/checkout"]');
    if (!checkout?.matches('[name="checkout"], a[href$="/checkout"]')) return;
    if (!checkout || !document.querySelector('[data-diw-role]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    checkout.disabled = true;
    try {
      await api.reconcile();
      location.assign(route('checkout'));
    } catch (err) {
      alert(err.message);
      checkout.disabled = false;
      checkingOut = false;
    }
  }
  document.addEventListener('click', checkoutHandler, true);
  document.addEventListener('submit', checkoutHandler, true);
})(typeof window === 'undefined' ? globalThis : window);
